import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Alert } from '@mui/material';
import {
  Edit as EditIcon,
  ContentCopy as CloneIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { agentService } from '../services/agentService';
import { useNotificationStore } from '../store/notificationStore';
import { ParameterList } from '../components/agents/ParameterList';
import { AgentMarkdownFileList } from '../components/agents/AgentMarkdownFileList';
import { AgentKnowledgeFileList } from '../components/agents/AgentKnowledgeFileList';
import { AgentDefinitionFunctionList } from '../components/agents/AgentDefinitionFunctionList';
import { AgentMcpServerList } from '../components/agents/AgentMcpServerList';
import { AgentSkillList } from '../components/agents/AgentSkillList';
import { AgentContextWindowPanel } from '../components/agents/AgentContextWindowPanel';
import { AttachedAgentsPanel } from '../components/orchestrator/AttachedAgentsPanel';
import { CodeEditor } from '../components/common/CodeEditor';
import { PageDetailLayout } from '../components/layouts/PageDetailLayout';
import type { AgentDefinitionDto, UpdateAgentParameterRequest, CreateAgentParameterRequest } from '../types/agent';

/**
 * Agent detail page component.
 */
export const AgentDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [agent, setAgent] = useState<AgentDefinitionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { addNotification } = useNotificationStore();

  useEffect(() => {
    if (id) {
      loadAgent();
    }
  }, [id]);

  const loadAgent = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const data = await agentService.getAgent(id);
      setAgent(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load agent');
      addNotification(err.response?.data?.message || 'Failed to load agent', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (agent && !agent.isSystem) {
      navigate(`/agents/${agent.id}/edit`);
    } else {
      addNotification('System agents cannot be edited', 'warning');
    }
  };

  const handleDelete = async () => {
    if (!agent || agent.isSystem) {
      addNotification('System agents cannot be deleted', 'warning');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${agent.name}"?`)) {
      return;
    }

    try {
      await agentService.deleteAgent(agent.id);
      addNotification('Agent deleted successfully', 'success');
      navigate('/agents');
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to delete agent', 'error');
    }
  };

  const handleClone = async () => {
    if (!agent) return;

    try {
      const cloned = await agentService.cloneAgent(agent.id);
      addNotification('Agent cloned successfully', 'success');
      navigate(`/agents/${cloned.id}`);
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to clone agent', 'error');
    }
  };

  const handleParameterAdd = async (_param: CreateAgentParameterRequest) => {
    if (!agent) return;
    addNotification('Parameter management will be available in edit mode', 'info');
  };

  const handleParameterUpdate = async (_paramId: string, _param: UpdateAgentParameterRequest) => {
    if (!agent) return;
    addNotification('Parameter management will be available in edit mode', 'info');
  };

  const handleParameterDelete = async (_paramId: string) => {
    if (!agent) return;
    addNotification('Parameter management will be available in edit mode', 'info');
  };

  return (
    <PageDetailLayout
      title={agent?.name || 'Agent'}
      breadcrumbs={[{ label: 'Agents', href: '/agents' }, { label: agent?.name || 'Agent' }]}
      backTo={{ label: 'Agents', href: '/agents' }}
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      icon={agent?.iconName ? <Typography variant="h5">{agent.iconName}</Typography> : undefined}
      status={agent ? { label: agent.isActive ? 'Active' : 'Inactive', color: agent.isActive ? 'success' : 'error' } : undefined}
      chips={agent ? [
        ...(agent.category ? [{ label: agent.category, variant: 'outlined' as const }] : []),
        { label: agent.isSystem ? 'System' : 'Custom', color: (agent.isSystem ? 'primary' : 'default') as 'primary' | 'default' },
        { label: `v${agent.version}`, variant: 'outlined' as const },
      ] : []}
      actions={agent ? [
        { icon: <CloneIcon />, label: 'Clone', onClick: handleClone },
        ...(!agent.isSystem ? [
          { icon: <EditIcon />, label: 'Edit', onClick: handleEdit },
          { icon: <DeleteIcon />, label: 'Delete', onClick: handleDelete, color: 'error' as const },
        ] : []),
      ] : []}
      tabs={agent ? [
        {
          label: 'Overview',
          value: 'overview',
          content: (
            <Box>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Description
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {agent.description || 'No description available'}
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 4 }} fontWeight={600}>
                Details
              </Typography>
              <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 2, mt: 2 }}>
                <Typography component="dt" fontWeight={500}>
                  Slug:
                </Typography>
                <Typography component="dd" color="text.secondary">
                  {agent.slug}
                </Typography>
                <Typography component="dt" fontWeight={500}>
                  Category:
                </Typography>
                <Typography component="dd" color="text.secondary">
                  {agent.category || 'Uncategorized'}
                </Typography>
                <Typography component="dt" fontWeight={500}>
                  Version:
                </Typography>
                <Typography component="dd" color="text.secondary">
                  {agent.version}
                </Typography>
                <Typography component="dt" fontWeight={500}>
                  Include All Default Tools:
                </Typography>
                <Typography component="dd" color="text.secondary">
                  {agent.includeAllDefaultTools ? 'Yes' : 'No'}
                </Typography>
                <Typography component="dt" fontWeight={500}>
                  Image context:
                </Typography>
                <Typography component="dd" color="text.secondary">
                  {(
                    {
                      ocrToContext: 'OCR / layout text',
                      descriptionToContext: 'AI image description',
                      ignoreImage: 'Ignore attachments',
                      visionOnlyNoCompaction: 'Vision only (no preprocessing)',
                    } as const
                  )[agent.imageContextMode ?? 'ocrToContext']}
                </Typography>
                <Typography component="dt" fontWeight={500}>
                  Context limit (last N messages):
                </Typography>
                <Typography component="dd" color="text.secondary">
                  {agent.contextLimitLastNMessages != null ? agent.contextLimitLastNMessages : 'No limit'}
                </Typography>
                <Typography component="dt" fontWeight={500}>
                  Context limit (last N days):
                </Typography>
                <Typography component="dd" color="text.secondary">
                  {agent.contextLimitLastNDays != null ? agent.contextLimitLastNDays : 'No limit'}
                </Typography>
              </Box>
            </Box>
          ),
        },
        {
          label: 'Configuration',
          value: 'configuration',
          content: (
            <Box>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                System Prompt
              </Typography>
              <CodeEditor value={agent.systemPrompt} language="plaintext" height="300px" readOnly={true} />

              {agent.isSystem && (
                <Alert severity="info" sx={{ mt: 3 }}>
                  System agents cannot be edited. Clone this agent to create a customizable version.
                </Alert>
              )}
            </Box>
          ),
        },
        {
          label: 'Context Window',
          value: 'context-window',
          content: (
            <AgentContextWindowPanel agent={agent} readOnly={agent.isSystem} />
          ),
        },
        {
          label: 'Parameters',
          value: 'parameters',
          content: (
            <ParameterList
              parameters={agent.parameters}
              onAdd={handleParameterAdd}
              onUpdate={handleParameterUpdate}
              onDelete={handleParameterDelete}
              readOnly={agent.isSystem}
            />
          ),
        },
        {
          label: 'File Search',
          value: 'file-search',
          content: (
            <AgentMarkdownFileList agentId={agent.id} readOnly={agent.isSystem} />
          ),
        },
        {
          label: 'Knowledge Files',
          value: 'knowledge-files',
          content: (
            <AgentKnowledgeFileList agentId={agent.id} readOnly={agent.isSystem} />
          ),
        },
        ...(agent.defaultModelProviderSupportsHostedSkills
          ? [
              {
                label: 'Skills',
                value: 'skills' as const,
                content: (
                  <AgentSkillList
                    agentId={agent.id}
                    readOnly={agent.isSystem}
                    providerId={agent.defaultModelProviderId}
                  />
                ),
              },
            ]
          : []),
        {
          label: 'Tools',
          value: 'functions',
          content: (
            <AgentDefinitionFunctionList
              agentId={agent.id}
              readOnly={agent.isSystem}
              initialFunctions={agent.functions}
            />
          ),
        },
        {
          label: 'MCP Servers',
          value: 'mcp-servers',
          content: (
            <AgentMcpServerList
              agentId={agent.id}
              readOnly={agent.isSystem}
            />
          ),
        },
        {
          label: 'Sub-Agents',
          value: 'sub-agents',
          content: (
            <AttachedAgentsPanel parentAgentId={agent.id} />
          ),
        },
      ] : []}
    />
  );
};
